import { useRef, useState } from "react";
import { CircleLoader } from "../CircleLoader";
import { defaultUrl } from "../../features/actions";
import { ParentForm } from "./ParentForm";
import { IParentForm } from "./ParentForm";
import { makeFetchRequest, makePostRequest } from "../../utilities/common";
import toast from "react-hot-toast";
import { ChevronRight, Plus, ShieldCheck, ArrowLeft } from "lucide-react";

const PRIMARY = "#0F2A6B";

export const Second = ({
  handleSubmit,
  nextStep,
  loading,
  handleChange,
  setLoading,
  form,
}: any) => {
  const [validateLoading, setValidateLoading] = useState<
    "idle" | "loading" | "error"
  >("idle");
  const [isParentExist, setIsParentExist] = useState([
    { status: false },
    { status: false },
  ]);
  const [newExistingData, setNewExistingData] = useState([[{ email: "" }]]);
  const [parentId] = useState<string[]>([]);
  const [newSelected, setNewSelected] = useState<string[]>([]);
  const [existingParents, setExistingParents] = useState<any[]>([]);
  const [selectedParent, setSelectedParent] = useState<any>({});
  const [isValidating, setIsValidating] = useState(false);
  const [parentIdArray, setParentIdArray] = useState([]);

  const [newParent, setNewParent] = useState<IParentForm[]>([
    {
      email: "",
      name: "",
      phone: 0,
      phone_number: "",
      home_number: "",
      relationship: "",
    },
  ]);

  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  /* ================= LOGIC ================= */

  async function checkForExistingParent(email: string) {
    const result = await makeFetchRequest(
      `${defaultUrl}/parents?email=${email}`,
      false,
      true,
    );
    setExistingParents(result);
    return result;
  }

  const handleNewParentChange = (
    index: number,
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { name, value } = event.target;

    // 1. Update local state immediately
    let data = [...newParent];
    (data[index] as any)[name] = value;

    // ✅ Ensure relationship is always stored correctly
    if (name === "relationship") {
      (data[index] as any).relationship = value;
    }

    setNewParent(data);

    // Reset selected parent if user edits again
    setSelectedParent({});

    // 2. Sync phone immediately
    if (index === 0 && name === "phone") {
      handleChange({
        name: "phone_number",
        value: value,
      });
    }

    // 3. Handle email validation (debounced)
    if (name === "email") {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);

      if (value.includes("@") && value.length > 5) {
        handleChange({
          name: "email",
          value: value,
        });

        debounceTimer.current = setTimeout(async () => {
          setValidateLoading("loading");
          setIsValidating(true);

          try {
            const existingParent: any[] = await checkForExistingParent(value);
            let parentStatus = [...isParentExist];

            if (existingParent.length > 0) {
              parentStatus[index].status = true;

              // Update form with DB values
              handleChange({
                name: "email",
                value: existingParent[0].email,
              });

              handleChange({
                name: "phone_number",
                value: existingParent[0].phone,
              });

              // Keep relationship intact ✅
              const updatedData = [...newParent];
              updatedData[index] = {
                ...updatedData[index],
                relationship: updatedData[index].relationship,
              };
              setNewParent(updatedData);

              // Update display data
              const newData = newExistingData.map((item: any, i: number) => {
                return index === i ? existingParent : item;
              });
              setNewExistingData(newData);
            } else {
              parentStatus[index].status = false;
            }

            setIsParentExist(parentStatus);
          } catch (error) {
            console.error("Parent validation failed", error);
          } finally {
            setValidateLoading("idle");
            setIsValidating(false);
          }
        }, 1000);
      }
    }

    // Cleanup
    setExistingParents([]);
  };

  const addFields = () => {
    if (newParent.length < 2) {
      setNewParent([
        ...newParent,
        {
          email: "",
          name: "",
          phone: 0,
          phone_number: "",
          home_number: "",
          relationship: "",
        },
      ]);
    }
  };

  const removeNewParent = (index: number) => {
    const data = [...newParent];
    data.splice(index, 1);
    setNewParent(data);
    const status = [...isParentExist];
    status.splice(index, 1);
    setIsParentExist(status);
  };

  const handleFinalSubmit = async (e: any) => {
    e.preventDefault();

    // Check if required fields are filled for the student before creating the parent
    const dataValue = [
      "surname",
      "firstname",
      "gender",
      "date_of_birth",
      "classroom_id",
      "house_address",
      "city",
      "state",
      "admission_date",
      "country",
    ];

    let dataValidated =
      dataValue.every((item) => form[item]?.length > 0) && form.profile_picture;

    if (!dataValidated) {
      toast.error("Some fields are required.");
      return; // Return early to prevent loading state change
    }

    setLoading(true); // Set loading only after validation passes

    const existingEmail = newExistingData.map((item) => item[0]?.email);
    const newParentToCreate = newParent.filter(
      (item) => !existingEmail.includes(item?.email),
    );

    const cleanedParentData = newParentToCreate.map((item: any) => ({
      email: item.email,
      name: item.name,
      phone: item.phone,
      phone_number: item.phone_number,
      home_number: item.home_number,
      relationship: item.relationship, // ✅ THIS is the fix
    }));

    if (cleanedParentData.length > 0) {
      await makePostRequest(`${defaultUrl}/parents/create`, {
        data: cleanedParentData,
      })
        .then((res) => {
          const newExistingParentIds = newSelected;
          const parentIDS = [...newExistingParentIds, ...res?.parent_id];
          handleSubmit(parentIDS);
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
      handleSubmit(newSelected);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <div className="max-w-4xl mx-auto px-6 py-10">
        {/* HEADER */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
          <div>
            <div className="flex items-center gap-2 text-blue-600 font-bold text-xs uppercase tracking-[0.2em] mb-2">
              <ShieldCheck size={14} />
              Final Step
            </div>
            <h1 className="text-3xl font-black text-slate-900 tracking-tight">
              Guardian Information
            </h1>
            <p className="text-slate-500 mt-2">
              Add at least one guardian for this pupil.
            </p>
          </div>

          <button
            type="button"
            onClick={() => nextStep(2)}
            className="flex items-center gap-2 px-5 py-2.5 bg-white border border-slate-200 rounded-xl text-slate-600 font-bold text-sm hover:bg-slate-50 transition-all shadow-sm"
          >
            <ArrowLeft size={16} /> Back
          </button>
        </div>

        {/* PROGRESS BAR */}
        <div className="bg-white rounded-full p-1 shadow-sm border border-slate-100 mb-12">
          <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-slate-900 rounded-full transition-all duration-1000"
              style={{ width: "100%" }}
            />
          </div>
        </div>

        <form onSubmit={handleFinalSubmit} className="space-y-8">
          {/* PARENT FORMS LIST */}
          <div className="space-y-6">
            {newParent.map((item, index) => (
              <ParentForm
                key={index}
                {...item}
                onChange={(event: any) => handleNewParentChange(index, event)}
                onAction={() => removeNewParent(index)}
                index={index}
                parentId={parentId}
                parentIdArray={parentIdArray}
                setParentIdArray={setParentIdArray}
                validateLoading={validateLoading}
                existingParents={existingParents}
                selectedParent={selectedParent}
                setSelectedParent={setSelectedParent}
                isParentExist={isParentExist}
                newExistingData={newExistingData}
                newSelected={newSelected}
                setNewSelected={setNewSelected}
              />
            ))}
          </div>

          {/* ADD BUTTON */}
          {newParent.length < 2 && (
            <button
              type="button"
              onClick={addFields}
              className="w-full py-8 border-2 border-dashed border-slate-200 rounded-3xl flex flex-col items-center justify-center gap-2 text-slate-400 hover:border-blue-400 hover:text-blue-500 hover:bg-blue-50/30 transition-all group"
            >
              <div className="p-2 bg-white rounded-full shadow-sm group-hover:bg-blue-100 transition-colors">
                <Plus size={20} />
              </div>
              <span className="font-bold text-sm">Add Secondary Guardian</span>
            </button>
          )}

          {/* FINAL CTA */}
          <div className="pt-8 border-t border-slate-200 flex flex-col md:flex-row items-center justify-between gap-6">
            <p className="text-sm text-slate-400 font-medium">
              Review information carefully before completing enrollment.
            </p>

            <button
              type="submit"
              disabled={loading}
              className="w-full md:w-auto px-10 py-4 bg-slate-900 text-white rounded-2xl font-bold flex items-center justify-center gap-3 hover:bg-black transition-all shadow-xl hover:shadow-2xl hover:-translate-y-1 active:translate-y-0 disabled:opacity-70"
            >
              {loading ? (
                <>
                  <CircleLoader /> Processing...
                </>
              ) : (
                <>
                  Complete Enrollment <ChevronRight size={18} />
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
